import {
  blueprintSubtypeGuidePromptLines,
  blueprintSubtypeSchema,
  projectFileClassificationTypeSchema,
  projectFileDocumentKindSchema,
  projectFileClassificationGuidePromptLines,
  projectFileDocumentKindGuidePromptLines,
} from '../models/project-file-batch.model';

/**
 * Purpose: segment an uploaded project file into classification rows and pick a
 *          whole-file `document_kind`; also surface name/description hints.
 *
 * Input:   file attachment (PDF or similar) + original filename. Content is
 *          usually Swedish construction documents (drawings, beskrivningar,
 *          areatabeller, förstudier).
 * Output:  structured output
 *          ({@link buildProjectFileClassifierStructuredOutputRootJsonSchema}).
 * Consumers:
 *   - `server/src/services/project-file-batches/project-file-classifier.ts`
 *     writes each segment + hints into `ProjectFile`.
 *   - Runtime extractors read segments to route per-type extraction.
 *
 * Schema is source of truth for per-field rules (nullability, enums,
 * descriptions). This prompt encodes cross-field logic and Swedish→enum mapping only.
 */
export const buildProjectFileClassifierInstructionBody = (): string => {
  const typeLines = projectFileClassificationTypeSchema.options
    .map(
      (t) => `  • ${t}:\n    ${projectFileClassificationGuidePromptLines(t)}`,
    )
    .join('\n');

  const documentKindLines = projectFileDocumentKindSchema.options
    .map((k) => `  • ${k}:\n    ${projectFileDocumentKindGuidePromptLines(k)}`)
    .join('\n');

  const blueprintSubtypeLines = blueprintSubtypeSchema.options
    .map((s) => `  • ${s}:\n    ${blueprintSubtypeGuidePromptLines(s)}`)
    .join('\n');

  return [
    'You classify Swedish construction / property project documents. Text and drawing titles are often Swedish — map them to the English enum keys below; keep the original Swedish wording in `evidence_excerpt` / `notes` when quoting.',
    '',
    'Emit one segment per distinct classification `type` present. The same upload may need several segments (mixed drawings, renderings, descriptions, area schedules, …).',
    '',
    'Buildings:',
    '- When the document makes multiple physical buildings explicit, assign each a stable id. One segment may list several ids when one sheet shows several buildings (a site plan, one plan that covers every house). Emit separate segments only when the sheets themselves are about different buildings.',
    '- Reuse the SAME id on every segment that refers to the SAME building (e.g. a plan + a section sheet). Use a NEW id for a different building.',
    '- Read the filename and the sheet title block. When the sheets show a building by name (e.g. "Hus 1", "Hus 1-3", "Hus A", "Kv 4", "Kvarter 2", "Byggnad 4", "Etapp B", "Huskropp 1", "Lamell C", "Building 2", "Block C"), set `building_label` to the EXACT in-document name and `building_instance_ids` to one slug per building: `hus_1`, `hus_a`, `kv_4`, `etapp_b`, `huskropp_1`, `lamell_c`, `block_c`. A range or list ("Hus 1-3", "Kv 1–6", "Kvarter 5 och 6") is one id per building, not one id for the range. `building_instance_id` repeats the first of those ids, or null when the list is empty. NEVER prefix an id with the project name. A name in the file name is not enough: a local detail, a foundation, or a cost calculation does not show a building volume and does not get an id.',
    '- The project name and stamp aliases of the same building are ONE building — reuse the same id. Mint a new id only when the documents distinguish separate buildings (Hus A vs Hus B, Kv 1 vs Kv 2, Etapp 1 vs Etapp 2).',
    '- A paper format in the stamp names the sheet. A token that is one segment of the drawing number names the sheet too, including when the stamp repeats it beside the scale or in the field a building name would occupy. Neither is a second building. Do not mint an id from them. Keep the id for the building the project name or the sheet title already names.',
    '- A filename or title that lists several buildings ("Hus 1-3", "Hus 8-9") means those buildings share the sheet. Put each id in `building_instance_ids` on that segment. Typologi N, fasadutsnitt, lägenhetstyp, and apartment-type indexes are NOT buildings — never mint an id from a typology number.',
    "- Accessory volumes on the same block's inner court (stair/elevator down to garage, ramp head, small entrance kiosk) are part of the main building already named on the sheet — list them in `accessory_volumes_on_main_block` and do NOT give them their own `building_instance_id`. A separate id is only for another building that has its own area-schedule BTA row or its own freestanding footprint on a site plan (several houses on one development).",
    '',
    'Per-segment `type` (output the snake_case enum value):',
    typeLines,
    '',
    '`title_block` (non-null only when `type` === "title_block"): the stamp is read here and nowhere else in the pipeline. `project_title` and `suggested_project_name` keep the printed project name, including a place printed with it. Do not shorten either to the building name.',
    '',
    'Per-blueprint `blueprint_subtype` (REQUIRED non-null when `type` === "blueprint"; null for every other type). Pick the subtype that matches the drawing\'s point of view:',
    blueprintSubtypeLines,
    '',
    'Whole-file `document_kind` — pick exactly ONE dominant kind for the upload. Use `mixed_project_bundle` when no single kind wins; `unknown_other` when unusable:',
    documentKindLines,
  ].join('\n');
};

const buildProjectFileClassifierFilenameBlock = (filename: string): string => {
  const safeFilename = JSON.stringify(filename.replace(/\r?\n/g, ' '));
  return [
    `Original file name: ${safeFilename}`,
    '',
    'Use this name together with the attachment as evidence (Swedish/English filenames are common industry hints).',
    '',
  ].join('\n');
};

/** Runtime: prepended to classifier messages with the resolved upload filename. */
export const projectFileClassifierFilenameBlockMarkdown = (
  fileName: string,
): string => buildProjectFileClassifierFilenameBlock(fileName);

/** Catalog: same skeleton with the literal `{{originalFilename}}` placeholder. */
export const projectFileClassifierFilenameBlockTemplate = (): string =>
  buildProjectFileClassifierFilenameBlock('{{originalFilename}}');
